/**
 * @file waiterCalls.service.ts
 * @description Manages acoustic service chimes and table assistance calls.
 */

import { supabase } from '../../../config/supabase.config';
import type { WaiterServiceCall } from '../../../types/waiter.types';

const CALL_CHIME_URL = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3';
const READY_CHIME_URL = 'https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3';

let callAudio: HTMLAudioElement | null = null;
let readyAudio: HTMLAudioElement | null = null;

export const playTableCallSound = (): void => {
  try {
    if (typeof window === 'undefined') return;
    if (!callAudio) callAudio = new Audio(CALL_CHIME_URL);
    callAudio.currentTime = 0;
    callAudio.play().catch(() => {});
  } catch {}
};

export const playOrderReadySound = (): void => {
  try {
    if (typeof window === 'undefined') return;
    if (!readyAudio) readyAudio = new Audio(READY_CHIME_URL);
    readyAudio.currentTime = 0;
    readyAudio.play().catch(() => {});
  } catch {}
};

export const fetchPendingCalls = async (venueId: string): Promise<WaiterServiceCall[]> => {
  // Restricts pending calls to requests initiated within the active 24-hour shift
  const cutoffIso = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

  // Queries waiter requests directly where table_id stores the table identifier, excluding cash payment flow requests
  const { data, error } = await supabase
    .from('waiter_requests')
    .select('id, venue_id, table_id, type, status, metadata, created_at')
    .eq('venue_id', venueId)
    .eq('status', 'pending')
    .neq('type', 'cash')
    .gte('created_at', cutoffIso)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[waiterCalls.service] Fetch error:', error);
    throw error;
  }

  return (data || []).map((row: any) => ({
    id: row.id,
    venue_id: row.venue_id,
    table_id: row.table_id,
    table_number: row.metadata?.table_number || row.table_id || 'T-?',
    type: row.type || 'call',
    status: row.status,
    created_at: row.created_at,
  }));
};

export const resolveServiceCall = async (requestId: string): Promise<void> => {
  const { error } = await supabase
    .from('waiter_requests')
    .update({ status: 'completed' })
    .eq('id', requestId);

  if (error) throw error;
};
